import { apiRequest } from "../../api/apiClient";
import type {
  ExSpouse,
  CreateExSpouseDto,
  UpdateExSpouseDto,
} from "../../types/exSpouse.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const exSpouseService = {
  getExSpousesByCertificateApplicationId: async (
    certificateApplicationId: number,
  ): Promise<ExSpouse[]> => {
    const response = await apiRequest(
      `/ExSpouses/certificate-application/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );

    if (response.status === 404 || response.status === 204) {
      return [];
    }

    const payload = await handleResponse<any>(response);

    if (Array.isArray(payload)) {
      return payload as ExSpouse[];
    }

    if (Array.isArray(payload?.data)) {
      return payload.data as ExSpouse[];
    }

    if (payload?.data && typeof payload.data === "object") {
      return [payload.data as ExSpouse];
    }

    if (payload && typeof payload === "object") {
      return [payload as ExSpouse];
    }

    return [];
  },

  createExSpouse: async (
    data: CreateExSpouseDto,
    photo?: File,
  ): Promise<ExSpouse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, String(value));
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/ExSpouses", {
      method: "POST",
      body: formData,
    });

    return handleResponse<ExSpouse>(response);
  },

  updateExSpouse: async (
    data: UpdateExSpouseDto,
    photo?: File,
  ): Promise<ExSpouse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, String(value));
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/ExSpouses", {
      method: "PUT",
      body: formData,
    });

    return handleResponse<ExSpouse>(response);
  },

  deleteExSpouse: async (id: number): Promise<void> => {
    let response = await apiRequest(`/ExSpouses/${id}`, {
      method: "DELETE",
    });

    if (response.status === 405) {
      response = await apiRequest("/ExSpouses", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
    }

    await handleResponse<void>(response);
  },
};
